import {Async, AsyncPage, Empty} from 'Core/models/Async';
import {DateRange, EmptyDateRange} from 'Core/models/DateRange';
import {Page} from 'Core/models/Page';
import {CorrespondenceLetterSummaryDto} from 'Agent/Policies/api';
import {CorrespondenceLetterSummary} from 'Agent/Policies/api/apiTypes';
import {CorrespondenceDetail} from 'Agent/Policies/models/CorrespondenceDetail';
import {createAction} from 'Shared/utilities/actionCreator';
import {ValidAgentNumber} from 'Users/models/AgentNumber';
import * as O from 'fp-ts/lib/Option';
import {Sort} from 'Shared/components/PagedView/Table/Sort';

export enum PolicyCorrespondenceActionType {
  GET_POLICY_CORRESPONDENCE_SUMMARIES = 'policyCorrespondence/GET_POLICY_CORRESPONDENCE_SUMMARIES',
  GET_POLICY_CORRESPONDENCE_DETAILS = 'policyCorrespondence/GET_POLICY_CORRESPONDENCE_DETAILS',
  CHANGE_POLICY_CORRESPONDENCE_SUMMARIES = 'policyCorrespondence/CHANGE_POLICY_CORRESPONDENCE_SUMMARIES',
  CHANGE_POLICY_CORRESPONDENCE_DETAILS = 'policyCorrespondence/CHANGE_POLICY_CORRESPONDENCE_DETAILS',
  CHANGE_SUMMARY_FILTER = 'policyCorrespondence/CHANGE_SUMMARY_FILTER',
  CLEAR_CORRESPONDENCE = 'policyCorrespondence/CLEAR_CORRESPONDENCE',
  CHANGE_SORT = 'policyCorrespondence/CHANGE_SORT',
  CLEAR_SORT = 'policyCorrespondence/CLEAR_SORT',
}

export type PolicyCorrespondenceSummaryFilter = {
  letterDescription: string;
  letterNumber: string;
  insuredName: string;
  policyNumber: string;
  accountNumber: string;
  agentNumber: string;
  letterDate: DateRange;
};

const EmptyPolicyCorrespondenceFilter: PolicyCorrespondenceSummaryFilter = {
  letterDescription: '',
  letterNumber: '',
  insuredName: '',
  policyNumber: '',
  accountNumber: '',
  agentNumber: '',
  letterDate: EmptyDateRange,
};

export type PolicyCorrespondenceDetailFilter = {
  agentNumbers: string | null;
  number: string | null;
  isNewYork: string | null;
};

export const changeSort = (sort?: Sort) => createAction(PolicyCorrespondenceActionType.CHANGE_SORT, {sort});
export const clearSort = () => createAction(PolicyCorrespondenceActionType.CLEAR_SORT);

export const getCorrespondenceSummaries = (
  agentNumbers: ValidAgentNumber[],
  isNewYork: boolean,
  encryptedIdentifier: string,
  page?: Page<CorrespondenceLetterSummary>,
  filter?: PolicyCorrespondenceSummaryFilter,
  sort?: Sort
) => {
  return createAction(PolicyCorrespondenceActionType.GET_POLICY_CORRESPONDENCE_SUMMARIES, {
    agentNumbers,
    isNewYork,
    encryptedIdentifier,
    page,
    filter,
    sort,
  });
};
export const clearCorrespondence = () => createAction(PolicyCorrespondenceActionType.CLEAR_CORRESPONDENCE);
export const getCorrespondenceDetails = (link: string) =>
  createAction(PolicyCorrespondenceActionType.GET_POLICY_CORRESPONDENCE_DETAILS, {link});
export const changeCorrespondenceSummaryFilter = (filter: PolicyCorrespondenceSummaryFilter) =>
  createAction(PolicyCorrespondenceActionType.CHANGE_SUMMARY_FILTER, {filter});
export const changeCorrespondenceSummaries = (
  correspondenceSummaries: AsyncPage<Page<CorrespondenceLetterSummaryDto>>
) => createAction(PolicyCorrespondenceActionType.CHANGE_POLICY_CORRESPONDENCE_SUMMARIES, correspondenceSummaries);
export const changeCorrespondenceDetails = (correspondenceDetails: Async<CorrespondenceDetail[]>) =>
  createAction(PolicyCorrespondenceActionType.CHANGE_POLICY_CORRESPONDENCE_DETAILS, correspondenceDetails);

type PolicyCorrespondenceState = {
  correspondenceSummaries: AsyncPage<Page<CorrespondenceLetterSummaryDto>>;
  correspondenceDetails: Async<CorrespondenceDetail[]>;
  correspondenceSummariesFilter: PolicyCorrespondenceSummaryFilter;
  sort: O.Option<Sort>;
};

type PolicyCorrespondenceAction =
  | ReturnType<typeof clearSort>
  | ReturnType<typeof changeSort>
  | ReturnType<typeof changeCorrespondenceSummaryFilter>
  | ReturnType<typeof changeCorrespondenceSummaries>
  | ReturnType<typeof changeCorrespondenceDetails>
  | ReturnType<typeof clearCorrespondence>;

const DEFAULT_STATE: PolicyCorrespondenceState = {
  correspondenceSummaries: new Empty(),
  correspondenceDetails: new Empty(),
  correspondenceSummariesFilter: EmptyPolicyCorrespondenceFilter,
  sort: O.none,
};

export default (
  state: PolicyCorrespondenceState = DEFAULT_STATE,
  action: PolicyCorrespondenceAction
): PolicyCorrespondenceState => {
  switch (action.type) {
    case PolicyCorrespondenceActionType.CLEAR_SORT:
      return {
        ...state,
        sort: DEFAULT_STATE.sort,
      };
    case PolicyCorrespondenceActionType.CHANGE_SORT:
      return {
        ...state,
        sort: O.fromNullable(action.payload.sort),
      };
    case PolicyCorrespondenceActionType.CLEAR_CORRESPONDENCE:
      return DEFAULT_STATE;
    case PolicyCorrespondenceActionType.CHANGE_SUMMARY_FILTER:
      return {
        ...state,
        correspondenceSummariesFilter: action.payload.filter,
      };
    case PolicyCorrespondenceActionType.CHANGE_POLICY_CORRESPONDENCE_SUMMARIES:
      return {
        ...state,
        correspondenceSummaries: action.payload,
      };
    case PolicyCorrespondenceActionType.CHANGE_POLICY_CORRESPONDENCE_DETAILS:
      return {
        ...state,
        correspondenceDetails: action.payload,
      };
    default:
      return state;
  }
};
